import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import { CommentModel } from '../../models';
import { CommentAction } from '../actions';

export interface CommentState {
  commentEntityState?: EntityState<CommentModel>;
  loadedThreads?: { [resourceType: string]: boolean };
  areAllLoaded?: boolean;
}

function selectCommentId(comment: CommentModel): string {
  return comment.id;
}

const commentAdapter: EntityAdapter<CommentModel> = createEntityAdapter<CommentModel>({
  selectId: selectCommentId,
});

const initialState: CommentState = {
  commentEntityState: commentAdapter.getInitialState(),
  loadedThreads: {},
  areAllLoaded: false,
};

const adapterReducer = createReducer(
  initialState,
  on(CommentAction.AllLoaded, (state, action) => {
    return {
      ...state,
      loadedResourceTypes: getResourceTypeIndex(action.comments),
      commentEntityState: commentAdapter.addMany(action.comments, commentAdapter.removeAll(state.commentEntityState)),
      areAllLoaded: true,
    };
  }),
  on(CommentAction.Created, (state, action) => {
    return {
      ...state,
      commentEntityState: commentAdapter.addOne(action.comment, state.commentEntityState),
    };
  }),
  on(CommentAction.Updated, (state, action) => {
    let newState = state;
    if (action.updatedComment && !action.updatedComment.isCreationInProgress && action.id) {
      newState = {
        ...state,
        commentEntityState: commentAdapter.removeOne(action.id, state.commentEntityState),
      };
    }
    return {
      ...newState,
      loadedResourceTypes: {
        ...state.loadedThreads,
        [action.updatedComment.threadId]: true,
      },
      commentEntityState: commentAdapter.updateOne(
        {
          id: action?.id ?? action.updatedComment.id,
          changes: action.updatedComment,
        },
        newState.commentEntityState,
      ),
    };
  }),
  on(CommentAction.UpdateFailed, (state, action) => {
    return {
      ...state,
      commentEntityState: commentAdapter.updateOne(
        {
          id: action.commentBeforeUpdate.id,
          changes: action.commentBeforeUpdate,
        },
        state.commentEntityState,
      ),
    };
  }),
  on(CommentAction.Deleted, (state, action) => {
    return {
      ...state,
      commentEntityState: commentAdapter.removeOne(action.deletedComment.id, state.commentEntityState),
    };
  }),
  on(CommentAction.DeleteFailed, (state, action) => {
    return {
      ...state,
      commentEntityState: commentAdapter.addOne(action.deletedComment, state.commentEntityState),
    };
  }),
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const commentReducer = (state = initialState, action: Action) => adapterReducer(state, action);

function getResourceTypeIndex(comments: CommentModel[]): {
  [resourceType: string]: true;
} {
  return comments
    .filter((comment) => comment.threadId)
    .reduce((result, comment) => ({ ...result, [comment.threadId]: true }), {});
}
